// Display-only vocabulary. Never use these decorations for artifact identity,
// source validation or saved entry text. Unknown/ambiguous names stay undecorated.
const FOOD = [
    // Vegetables
    ['🥒', 'cucumbers?'], ['🍅', 'tomato(?:es)?|cherry tomatoes'],
    ['🧄', 'garlic|garlic cloves?|garlic powder'], ['🧅', 'onions?|red onions?|spring onions?|shallots?|leeks?'],
    ['🥕', 'carrots?'], ['🥔', 'potato(?:es)?'], ['🍠', 'sweet potato(?:es)?'],
    ['🥦', 'broccoli'], ['🥬', 'lettuce|spinach|kale|cabbage'],
    ['🍄', 'mushrooms?'], ['🥑', 'avocado(?:s)?'], ['🌽', 'corn|sweetcorn'],
    ['🍆', 'eggplants?|aubergines?'], ['🫑', 'bell peppers?|red bell peppers?|green bell peppers?'],
    ['🌶️', 'chill?i(?:es)?|chill?i peppers?'],
    // Fruit
    ['🍋', 'lemons?'], ['🍊', 'oranges?'], ['🍎', 'apples?'], ['🍌', 'bananas?'],
    ['🍓', 'strawberr(?:y|ies)'], ['🫐', 'blueberr(?:y|ies)'],
    ['🥥', 'coconuts?'],
    // Dairy & eggs
    ['🥚', 'eggs?'], ['🥛', 'milk|whole milk|skimmed milk'], ['🧈', 'butter|unsalted butter|salted butter'],
    ['🧀', 'cheese|feta|feta cheese|cheddar|parmesan|mozzarella'],
    // Grains & baked
    ['🍞', 'bread|sourdough bread'], ['🥯', 'bagels?'], ['🥐', 'croissants?'],
    ['🍚', 'rice|basmati rice|jasmine rice|brown rice|white rice'],
    ['🌾', 'oats?|rolled oats?|oatmeal|flour|bread flour|all-purpose flour'],
    ['🍝', 'pasta|spaghetti|noodles?'],
    // Meat & fish
    ['🍗', 'chicken|chicken thighs?|chicken breasts?|chicken drumsticks?|turkey|turkey breasts?|duck'],
    ['🥩', 'beef|steaks?|ground beef|minced? beef|mince|pork|lamb|veal|brisket|ribs?|pork chops?|lamb chops?|pork loin|pork belly'],
    ['🥓', 'bacon|ham|prosciutto'],
    ['🌭', 'sausages?|chorizo|salami|pepperoni'],
    ['🐟', 'fish|salmon|tuna|cod|tilapia|halibut|sea bass|trout'],
    ['🍤', 'shrimp|prawns?'],
    // Condiments, oils & liquids
    ['🫒', 'olive oil|vegetable oil|coconut oil|sesame oil'],
    ['🧃', 'lemon juice|lime juice|orange juice'],
    ['🍷', 'red wine|white wine|wine'],
    ['🍯', 'honey'], ['🧂', 'salt|sea salt'],
    ['🥜', 'peanuts?'],
    // Herbs & spices
    ['🌿', 'herbs?|parsley|cilantro|coriander|mint|dill'],
    ['🫙', 'cinnamon|vanilla|nutmeg|turmeric|paprika|cumin|oregano|basil|thyme|rosemary|bay leaves?'],
    // Drinks
    ['☕', 'coffee'], ['🍵', 'tea'],
];
const PACKING = [
    ['🔌', 'chargers?|phone chargers?|charging cables?|power adapters?'],
    ['👕', 'shirts?|t-shirts?|tops?'], ['👖', 'jeans|trousers|pants'],
    ['🧦', 'socks'], ['👟', 'shoes|trainers|sneakers'], ['🧥', 'jackets?|coats?'],
    ['🪥', 'toothbrush(?:es)?'], ['🧴', 'sunscreen|shampoo|conditioner'],
    ['🕶️', 'sunglasses'], ['🎧', 'headphones|earbuds'], ['📷', 'cameras?'],
    ['💻', 'laptops?'], ['📱', 'phones?|mobile phones?'], ['🎒', 'backpacks?']
];
const TOOLS = [
    ['🔧', 'wrench(?:es)?|spanners?'], ['🔨', 'hammers?'],
    ['🪛', 'screwdrivers?|phillips screwdrivers?|flathead screwdrivers?'],
    ['🪚', 'saws?|handsaws?'], ['✂️', 'scissors'],
    ['🪵', 'wood|timber|wooden boards?'], ['🧱', 'bricks?'],
    ['🪣', 'buckets?'], ['🧽', 'sponges?'], ['🧤', 'gloves|work gloves|safety gloves']
];
const compile = vocabulary => vocabulary.map(([emoji, names]) => [emoji, new RegExp(`^(?:${names})$`, 'i')]);
const food = compile(FOOD);
const packing = compile(PACKING);
const tools = compile(TOOLS);
const hasEmoji = value => /\p{Extended_Pictographic}/u.test(String(value || ''));
const match = (value, vocabulary) => {
    // Commas and colons introduce preparation notes; match the item name, never
    // substrings inside unrelated products (e.g. coconut milk, peanut butter,
    // chicken soup). Colon splits "Apples: Grated" style entries from the model.
    const name = String(value || '').normalize('NFKC').split(/[,:]/)[0].trim()
        .replace(/^(?:(?:fresh|frozen|dried|chopped|diced|sliced|grated|crumbled|boneless|skinless|raw|whole|old-fashioned|rolled|plain|organic|unsweetened)\s+)+/i, '');
    return vocabulary.find(([, pattern]) => pattern.test(name))?.[0] || null;
};

export const getArtifactEntryEmoji = (artifact, section, entry) => {
    if (entry?.emoji && hasEmoji(entry.emoji)) return entry.emoji.trim();
    if (hasEmoji(entry?.text) || hasEmoji(entry?.label)) return null;
    const type = artifact?.type;
    const kind = section?.kind;
    let vocabulary;
    if (type === 'recipe' && ['items', 'materials'].includes(kind)) vocabulary = food;
    if (type === 'shopping_list' && ['items', 'materials'].includes(kind)) vocabulary = [...food, ...packing, ...tools];
    if (type === 'packing_list' && kind === 'items') vocabulary = [...packing, ...tools, ...food];
    if (['tutorial', 'repair', 'procedure'].includes(type) && ['tools', 'materials'].includes(kind)) vocabulary = tools;
    return vocabulary ? match(entry.text || entry.label, vocabulary) : null;
};

export const getArtifactSectionEmoji = (artifact, section) => {
    if (artifact?.type !== 'workout' || hasEmoji(section?.title)) return null;
    return match(section?.title, compile([
        ['🔥', 'warm[ -]?up'], ['🧘', 'cool[ -]?down|stretching|mobility'],
        ['💪', 'strength|strength training'], ['🏃', 'cardio']
    ]));
};

export const getArtifactMetadataEmoji = (artifact, item) => {
    if (!['event', 'place'].includes(artifact?.type) || hasEmoji(item?.label)) return null;
    return ({ address: '📍', location: '📍', startDate: '📅', endDate: '📅', date: '📅',
        startTime: '🕒', endTime: '🕒', timeZone: '🌐', website: '🌐', rating: '⭐' })[item?.key] || null;
};
